import React from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  styled,
  Typography
} from '@mui/material';
import {
  activityTemplates,
  appEvents,
  AuthWrapper,
  LoadingOverlay,
  routeDirectory,
  TabBar
} from 'modules';
import {
  useNavigate,
  useSearchParams
} from 'react-router-dom';

import { AddCircle } from '@mui/icons-material';
import * as InvestmentAreasTabPane from './InvestmentAreasTabPane/InvestmentAreasTabPane';
import * as InvestorsTabPane from './InvestorsTabPane/InvestorsTabPane';
import * as StartupsTabPane from './StartupsTabPane/StartupsTabPane';
import * as AccessPassesTabPane from './AccessPassesTabPane/AccessPassesTabPane';
import * as AppSettingsTabPane from './AppSettingsTabPane/AppSettingsTabPane';



const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2)
}));

export type ITabPane = 'investors' | 'startups' | 'investmentAreas' | 'accessPasses' | 'appSettings';

export const SuperAdminDashboardActivity: React.FC = props => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [childLoading, setChildLoading] = React.useState(false);

  const tab = React.useMemo((): ITabPane => params.get('tab') as ITabPane | null ?? 'investors', [params]);



  function handleClickCreate() {
    if (tab === 'investors' || tab === 'startups') {
      appEvents.broadcast({
        type: 'modal',
        args: {
          type: 'uploadUsers',
          userType: tab
        }
      });

    } else if (tab === 'investmentAreas') {
      appEvents.broadcast({
        type: 'modal',
        args: {
          type: 'uploadInvestmentAreas'
        }
      });

    } else if (tab === 'accessPasses') {
      appEvents.broadcast({
        type: 'modal',
        args: {
          type: 'editAccessPass'
        }
      });
    }
  }



  return (
    <AuthWrapper.FC superAdminOnly>
      <activityTemplates.SuperAdminActivityTemplate.FC>
        <LoadingOverlay active={childLoading} />

        <Stack spacing={3}>
          <Typography variant="h1">Dashboard</Typography>

          <Typography>Manage all of your users here.</Typography>

          <Card>
            <CardContent>
              <HeaderBox>
                <TabBar.FC
                  onClickTab={tab => {
                    navigate(routeDirectory.superAdmin.dashboard.path(tab), { replace: true });
                  }}
                  selectedTabId={tab ?? 'investors'}
                  tabs={[
                    {
                      id: 'investors',
                      label: 'Investors'
                    },
                    {
                      id: 'startups',
                      label: 'Startups'
                    },
                    {
                      id: 'investmentAreas',
                      label: 'Investment Areas'
                    },
                    {
                      id: 'accessPasses',
                      label: 'Access Passes'
                    },
                    {
                      id: 'appSettings',
                      label: 'App Settings'
                    }
                  ]} />

                {tab && tab !== 'appSettings' && (
                  <Button
                    startIcon={<AddCircle />}
                    size="small"
                    onClick={handleClickCreate}>
                    Add {__tab2ButtonLabel[tab]}
                  </Button>
                )}
              </HeaderBox>


              {tab === 'investors' && (
                <InvestorsTabPane.FC
                  onChangeLoading={setChildLoading} />
              )}

              {tab === 'startups' && (
                <StartupsTabPane.FC
                  onChangeLoading={setChildLoading} />
              )}

              {tab === 'investmentAreas' && (
                <InvestmentAreasTabPane.FC
                  onChangeLoading={setChildLoading} />
              )}

              {tab === 'accessPasses' && (
                <AccessPassesTabPane.FC
                  onChangeLoading={setChildLoading} />
              )}

              {tab === 'appSettings' && (
                <AppSettingsTabPane.FC
                  onChangeLoading={setChildLoading} />
              )}
            </CardContent>
          </Card>
        </Stack>

      </activityTemplates.SuperAdminActivityTemplate.FC>
    </AuthWrapper.FC>
  );
};



const __tab2ButtonLabel: Record<ITabPane, string> = {
  accessPasses: 'Access Passes',
  appSettings: '',
  investmentAreas: 'Investment Areas',
  investors: 'Investors',
  startups: 'Startups'
};
